import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Axios from "axios";
import Calendar from "@/user/components/fair/calendar";
import { sliderSetting, setColorClass } from "@/shared/calendar";
import route from "@/shared/ziggy/route";

/**
 * APIアクセス
 * @param {array} $params
 * @returns {json}
 */
const api = ($params) => {
  const endpoint = route("api.user.fair.count");

  return Axios.get(endpoint, {
    params: $params,
  });
};

/**
 * フェア絞り込み検索の条件設定
 * @param {array} selectFairTypes 指定されたフェア内容のID
 * @param {array} selectFairDate 指定されたカレンダーの日付
 * @return {array}
 */
const fairParams = (selectFairTypes, selectFairDate) => {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    fairTypes: selectFairTypes,
    groupBy: searchParams.has("group_by") ? "id" : "",
    dates: selectFairDate,
  };
};
/**
 *フェアAPIの実行
 * @param {array} selectFairTypes 指定されたフェア内容のID
 * @param {array} selectFairDate 指定されたカレンダーの日付
 * @param setFairCount フェア件数の更新処理
 * @param {number} fairsTotal 該当するフェア件数
 */
const handleFairAPIRequest = (
  selectFairTypes,
  selectFairDate,
  setFairCount,
  fairsTotal,
) => {
  api(fairParams(selectFairTypes, selectFairDate)).then(({ data }) => {
    if (!data) {
      setFairCount(fairsTotal);
      return;
    }
    // 予約ボタンにカウントの表示
    setFairCount(data.fair_count);
  });
};

/**
 *
 * @param fairDays
 * @param fairTypes
 * @param startOfMonth
 * @param webReservePeriodDate
 * @param fairsTotal
 * @param initialDate
 * @param initialFairType
 * @returns {JSX.Element}
 * @constructor
 */
function SearchContainer({
  fairDays,
  fairTypes,
  startOfMonth,
  webReservePeriodDate,
  fairsTotal,
  initialDate,
  initialFairType,
  holidays,
}) {
  // モーダルの表示非表示
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 該当するフェアの件数
  const [fairCount, setFairCount] = useState(fairsTotal);
  // フェア開催内容
  const [selectedTypes, setSelectedTypes] = useState(initialFairType);
  // カレンダーの日付
  const [selectedFairDates, setSelectedFairDates] = useState(initialDate);

  // カレンダーのカルーセル
  sliderSetting(true, false);

  // 開催内容の選択操作
  const handleFairTypeClick = (e) => {
    const { checked, value } = e.target;

    setSelectedTypes((prevTypes) => {
      if (checked) {
        return [...prevTypes, value];
      }
      return prevTypes.filter((type) => type !== value);
    });
  };

  // 日付選択操作
  const handleCalendarDayClick = (e) => {
    const { checked, value } = e.target;
    setSelectedFairDates((prevDates) => {
      if (checked) {
        return [...prevDates, value];
      }
      return prevDates.filter((d) => d !== value);
    });
  };

  // 選択をクリアする操作
  const handleCancel = (e) => {
    e.preventDefault();
    // checked を外す
    const checkedInputs = document.querySelectorAll(
      "#js-calendar_fair_search input[type=checkbox]:checked",
    );
    checkedInputs.forEach((checkedInput) => {
      // eslint-disable-next-line no-param-reassign
      checkedInput.checked = false;
    });
    // カレンダーの色調整
    setColorClass();
    // フェア内容の選択を解除
    setSelectedTypes([]);
    setSelectedFairDates([]);
  };

  // モーダルを閉じる操作
  const handleModalClose = () => {
    // フェア内容の選択をオープンの初期状態に戻す
    setSelectedTypes(initialFairType);
    setSelectedFairDates(initialDate);
    // モーダル閉じる
    setIsModalOpen(false);
  };

  useEffect(() => {
    // API実行
    handleFairAPIRequest(
      selectedTypes,
      selectedFairDates,
      setFairCount,
      fairsTotal,
    );
  }, [selectedTypes, selectedFairDates, fairsTotal]);

  useEffect(() => {
    const openModal = document.getElementById("js-modal_search_open");
    // カレンダーの日付
    const inputDateCheckBox = document.querySelectorAll(
      '#js-calendar_fair_search input[name="date[]"]',
    );

    const handleOpenModal = () => {
      setIsModalOpen(true);
    };

    if (inputDateCheckBox) {
      inputDateCheckBox.forEach((checkDay) => {
        checkDay.addEventListener("click", handleCalendarDayClick);
      });
    }
    if (openModal) {
      openModal.addEventListener("click", handleOpenModal);
    }
    return () => {
      if (inputDateCheckBox) {
        inputDateCheckBox.forEach((checkDay) => {
          checkDay.addEventListener("click", handleCalendarDayClick);
        });
      }
      if (openModal) {
        openModal.removeEventListener("click", handleOpenModal);
      }
    };
  }, []);

  return (
    <div className={`fair-modal ${isModalOpen ? "is-active" : ""}`}>
      <div className="fair-modal__overlay" onClick={handleModalClose} />
      <div className="fair-modal__container">
        <div
          className="fair-modal__close color--text"
          onClick={handleModalClose}
        >
          ×
        </div>
        <div className="fair-modal__content background-color--page_bg">
          <div id="fair-search" className="fair-search">
            <div className="fair-search__head">
              <p className="fair-search__title color--accent font-family--middle">
                日程と内容からフェアを探す
              </p>
            </div>
            {/* カレンダー表示 */}
            <Calendar
              startOfMonth={startOfMonth}
              fairDays={fairDays}
              webReservePeriodDate={webReservePeriodDate}
              selectedDate={selectedFairDates}
              type={"fair_search"}
              holidays={holidays}
            />
            <p className="fair-search__subtitle color--text font-family--middle">
              内容を選択
            </p>
            <ul className="fair-search__type">
              {Object.entries(fairTypes).map((type, i) => {
                const fairType = type[1];
                const isChecked = selectedTypes.includes(
                  fairType.id.toString(),
                );

                return (
                  <li key={i} className="fair-search__list">
                    <input
                      type="checkbox"
                      name="fair_type[]"
                      value={fairType.id}
                      checked={isChecked}
                      id={`fair_type_${fairType.id}`}
                      onChange={handleFairTypeClick}
                    />
                    <label
                      htmlFor={`fair_type_${fairType.id}`}
                      className={`font-family--middle ${isChecked ? "background-color--sub border-color--sub" : "border-color--text-before::before background-color"}`}
                    >
                      {fairType.name}
                    </label>
                  </li>
                );
              })}
            </ul>
            <ul className="fair-search__btn">
              <button
                type="button"
                className="action-btn--cancel border-color--accent color--accent font-family--middle background-color--page_bg"
                id="js-calendar_cancel"
                onClick={(e) => handleCancel(e)}
              >
                選択をクリア
              </button>
              <button
                type="submit"
                className="action-btn--submit background-color--accent border-color--accent font-family--middle"
                name="fair-search"
                value="search_form"
              >
                該当するフェアを見る（
                <span id="js-search-count">{fairCount}</span>件）
              </button>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * fairDays: 開催日付と時間一覧
 * fairTypes: 開催内容
 * startOfMonth: カレンダー開始日(YYYY-mm-dd)
 * webReservePeriodDate: オンライン予約の受付期限（日数）
 * fairsTotal: フェアの件数
 */
SearchContainer.propTypes = {
  fairDays: PropTypes.object.isRequired,
  fairTypes: PropTypes.object.isRequired,
  startOfMonth: PropTypes.string.isRequired,
  webReservePeriodDate: PropTypes.string.isRequired,
  fairsTotal: PropTypes.number,
};

export default SearchContainer;
